<template>
    <figure class="rich-chart">
        <canvas ref="canvas" role="img" :aria-label="ariaLabel"></canvas>
        <figcaption v-if="title" class="details text-ink-muted">{{ title }}</figcaption>
        <!-- Canvas tooltips are pointer-only — the values list keeps every pair reachable by keyboard. -->
        <BaseCollapsible class="chart-data" :unmount-on-hide="false">
            <template #trigger>
                <span class="chart-data-summary details text-ink-muted">
                    <i class="chart-data-chevron bi bi-caret-right-fill" aria-hidden="true" />{{ t("artifacts.panel.chartValues") }}
                </span>
            </template>
            <template #content>
                <ul class="details text-ink-muted">
                    <li v-for="(label, i) in labels" :key="label">{{ label }}: {{ formatValue(values[i] ?? 0) }}</li>
                </ul>
            </template>
        </BaseCollapsible>
    </figure>
</template>

<script setup lang="ts">
import BaseCollapsible from "~vue/components/BaseCollapsible.vue";
import { i18n } from "~vue/i18n";
import { formatNumber } from "~vue/utils/formatNumber";
import { theme } from "~vue/utils/theme";
import { BarController, BarElement, CategoryScale, Chart, LinearScale, Tooltip } from "chart.js";
import { computed, onBeforeUnmount, onMounted, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";

import type { RichBarChart } from "./types";

const props = defineProps<Omit<RichBarChart, "type">>();
const { t } = useI18n();
const canvas = useTemplateRef<HTMLCanvasElement>("canvas");

const formatValue = (value: number) => `${formatNumber(value, "decimal")}${props.unit ? ` ${props.unit}` : ""}`;

// The title alone never names the bars — the label always carries the values too.
const ariaLabel = computed(() => {
    const series = props.labels.map((label, i) => `${label}: ${formatValue(props.values[i] ?? 0)}`).join(", ");
    return props.title ? `${props.title} — ${series}` : series;
});

// Thin marks, 24px cap, 4px rounded tip square at the baseline, hairline y-grid only, text in
// ink tokens and the page font: the chart reads as part of the document, not as a Chart.js default.
const MAX_BAR_THICKNESS = 24;
const BAR_TIP_RADIUS = 4;

// Resolved on the canvas so the chart follows the active theme; empty (jsdom, a detached node)
// leaves Chart.js's own default rather than painting with an empty string.
const token = (styles: CSSStyleDeclaration, name: string): string | undefined => styles.getPropertyValue(name).trim() || undefined;

let chart: Chart<"bar"> | null = null;

const render = () => {
    const el = canvas.value;
    if (!el) {
        return;
    }
    chart?.destroy();
    const styles = globalThis.getComputedStyle(el);
    const border = token(styles, "--color-border");
    const inkMuted = token(styles, "--color-ink-muted");
    const font = { family: styles.fontFamily || undefined };
    chart = new Chart(el, {
        type: "bar",
        data: {
            labels: props.labels,
            datasets: [
                {
                    data: props.values,
                    backgroundColor: styles.color,
                    borderWidth: 0,
                    borderRadius: BAR_TIP_RADIUS,
                    borderSkipped: "bottom",
                    maxBarThickness: MAX_BAR_THICKNESS,
                },
            ],
        },
        options: {
            plugins: {
                legend: { display: false },
                tooltip: {
                    titleFont: font,
                    bodyFont: font,
                    // The default label is the raw number — format like the axis ticks (unit + locale).
                    callbacks: { label: (item) => formatValue(Number(item.parsed.y)) },
                },
            },
            scales: {
                x: {
                    grid: { display: false },
                    border: { color: border },
                    ticks: { color: inkMuted, font },
                },
                y: {
                    beginAtZero: true,
                    grid: { color: border, lineWidth: 1 },
                    border: { display: false },
                    ticks: { color: inkMuted, font, maxTicksLimit: 5, callback: (v) => formatValue(Number(v)) },
                },
            },
            maintainAspectRatio: true,
            aspectRatio: 2,
        },
    });
};

Chart.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip);
onMounted(render);
watch(() => [props.labels, props.values, props.unit], render, { deep: true });
// Canvas ticks don't re-render on their own when the interface language changes, and the colours
// are read once at construction, so a theme switch needs a fresh chart too.
watch(() => i18n.global.locale.value, render);
watch(theme, render);
onBeforeUnmount(() => chart?.destroy());
</script>

<style scoped>
@reference "../../../../../css/tailwind.css";

@layer components {
    .rich-chart {
        @apply flex flex-col gap-2 text-accent;
    }

    /* Mirrors the native details marker this disclosure replaced. */
    .chart-data-summary {
        @apply inline-flex cursor-pointer items-center gap-1;
    }

    .chart-data-chevron {
        @apply text-[0.625rem] transition-transform;
    }

    .chart-data[data-state="open"] .chart-data-chevron {
        @apply rotate-90;
    }
}

@media (prefers-reduced-motion: reduce) {
    .chart-data-chevron {
        transition: none;
    }
}
</style>
